/**
 * Visszatérítések — handover rev7 §11.7 and §9.8: "Open refunds by due date;
 * mark done with date and reference."
 *
 * The date is the server's `app_now()`, not a field on this form: a refund
 * marked done is marked done now, and a console that let the date be typed
 * would be a console where it can be typed wrong. The reference is mandatory.
 *
 * §9.8 also says no customer email goes out on marking one done, and that
 * absence is the rule — so there is no "notify" control here, deliberately.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { asMoment, fmt, Loading, useConsoleAction, useConsoleData } from '../common.tsx'

interface Refund {
  id: string
  order_id: string
  order_ref: string
  customer_name: string
  amount: number
  provider: string
  provider_ref: string | null
  due_at: string | null
  done_at: string | null
  overdue: boolean
  note: string | null
  created_at: string
}

export function Refunds(): JSX.Element {
  const [includeDone, setIncludeDone] = useState(false)
  const { data, error, loading, reload } = useConsoleData<{ refunds: Refund[] }>(
    'refunds',
    includeDone ? { include_done: true } : {},
  )
  const { run, busy, error: actionError } = useConsoleAction()
  const [reference, setReference] = useState<Record<string, string>>({})

  async function markDone(refund: Refund): Promise<void> {
    if (
      await run('refund_done', {
        payment_id: refund.id,
        reference: reference[refund.id] ?? '',
      })
    ) {
      setReference((previous) => ({ ...previous, [refund.id]: '' }))
      reload()
    }
  }

  return (
    <section data-testid="view-refunds">
      <h2>Visszatérítések</h2>
      <p className="lead">
        §9.8: a visszatérítést a pénzügy indítja a szolgáltató felületén, és itt jelöli
        elvégzettnek a hivatkozással. A vendég erről nem kap külön e-mailt.
      </p>

      <div className="actions">
        <button
          type="button"
          onClick={() => setIncludeDone((value) => !value)}
          data-testid="toggle-done"
        >
          {includeDone ? 'Csak a nyitottak' : 'Az elvégzettek is'}
        </button>
      </div>

      <Loading loading={loading} error={error} />
      {actionError !== null && <p className="note error" data-testid="action-error">{actionError}</p>}

      {data !== null && data.refunds.length === 0 && (
        <p className="note" data-testid="refunds-empty">Nincs nyitott visszatérítés.</p>
      )}

      {data !== null && data.refunds.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Rendelés</th>
              <th className="num">Összeg</th>
              <th>Határidő</th>
              <th>Tranzakció</th>
              <th>Elvégezve</th>
            </tr>
          </thead>
          <tbody>
            {data.refunds.map((refund) => (
              <tr key={refund.id} data-testid={`refund-${refund.order_ref}`}>
                <td>
                  <Link to={`/staff/orders/${refund.order_id}`}>{refund.order_ref}</Link>
                  <div className="tag">{refund.customer_name}</div>
                </td>
                <td className="num">{fmt.money(refund.amount)}</td>
                <td>
                  {asMoment(refund.due_at)}
                  {refund.overdue && <div className="tag stop">lejárt (F7)</div>}
                </td>
                <td>{refund.provider_ref ?? refund.provider}</td>
                <td>
                  {refund.done_at !== null
                    ? (
                      <>
                        <span className="tag ok">{asMoment(refund.done_at)}</span>
                        <div className="tag">{refund.note ?? ''}</div>
                      </>
                    )
                    : (
                      <>
                        <label htmlFor={`ref-${refund.id}`}>Hivatkozás</label>
                        <input
                          id={`ref-${refund.id}`}
                          type="text"
                          value={reference[refund.id] ?? ''}
                          onChange={(event) =>
                            setReference((previous) => ({
                              ...previous,
                              [refund.id]: event.target.value,
                            }))}
                          data-testid={`refund-ref-${refund.order_ref}`}
                        />
                        <div className="actions">
                          <button
                            type="button"
                            className="primary"
                            disabled={busy || (reference[refund.id] ?? '').trim() === ''}
                            onClick={() => void markDone(refund)}
                            data-testid={`refund-done-${refund.order_ref}`}
                          >
                            Elvégezve
                          </button>
                        </div>
                      </>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
